import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Why programmers work at night",
  "description": "",
  "published": "2011-12-15T00:00:00.000Z",
  "content_upgrade": "SeniorMindset",
  "redirect_from": ["/blog/why-programmers-work-at-night/swizec/3198"],
  "categories": "Business, Computer programming, Freelancing, Opinions, Paul Graham, Personal, Programmer, Programming, Side Projects, Source code",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`[`}{`This essay has been expanded into a book, you should read it, `}<a parentName="em" {...{
          "href": "http://nightowlsbook.com"
        }}>{`here`}</a>{`]`}</em></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/300px-Chimp_Brain_in_a_jar8.jpg",
        "alt": "A chimpanzee brain at the Science Museum London",
        "title": "A chimpanzee brain at the Science Museum London"
      }}></img></p>
    <p>{`A popular saying goes that `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Programmer",
        "title": "Programmer"
      }}>{`Programmers`}</a>{` are machines that turn caffeine into `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Source_code",
        "title": "Source code"
      }}>{`code`}</a>{`.`}</p>
    <p>{`And sure enough, ask a random programmer when they do their best work and there's a high chance they will admit to a lot of late nights. Some earlier, some later. A popular trend is to get up at 4am and get some work done before the day's craziness begins. Others like `}<em parentName="p">{`going`}</em>{` to bed at 4am.`}</p>
    <p>{`At the gist of all this is avoiding distractions. But you could just lock the door, what's so special about the night?`}</p>
    <p>{`I think it boils down to three things: the maker's schedule, the sleepy brain and bright computer screens.`}</p>
    <h2 {...{
      "id": "the-makers-schedule"
    }}>{`The maker's schedule`}</h2>
    <p><a parentName="p" {...{
        "href": "http://paulgraham.com",
        "title": "Paul Graham (computer programmer)"
      }}>{`Paul Graham`}</a>{` wrote about `}<a parentName="p" {...{
        "href": "http://www.paulgraham.com/makersschedule.html"
      }}>{`the maker's schedule`}</a>{` in 2009 - basically that there are two types of schedules in this world (primarily?). The traditional manager's schedule where your day is cut up into hours and a ten minute distraction costs you, at most, an hour's worth of time.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/300px-Prim_clockwork4.jpg",
        "alt": "Prim clockwork of a wristwatch, watchmaking ex...",
        "title": "Prim clockwork of a wristwatch, watchmaking ex..."
      }}></img></p>
    <p>{`On the other hand you have something PG calls the maker's schedule - a schedule for those of us who produce stuff. Working on large abstract systems involves fitting the whole thing into your mind - somebody once likened this to constructing a house out of expensive `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Lead_glass",
        "title": "Lead glass"
      }}>{`crystal glass`}</a>{`and as soon as someone distracts you, it all comes barreling down and shatters into a thousand pieces.`}</p>
    <p>{`This is why programmers are so annoyed when you distract them.`}</p>
    <p>{`Because of this huge mental investment, we simply can't start working until we can expect a couple of hours without being distracted. It's just not worth constructing the whole model in your head and then having it torn down half an hour later.`}</p>
    <p>{`In fact, talking to a lot of founders you'll find out they feel like they simply can't get any work done during the day. The constant barrage of interruptions, important stuff (tm) to tend to and emails to answer simply don't allow it. So they get most of their "work work" done during the night when everyone else is sleeping.`}</p>
    <h2 {...{
      "id": "the-sleepy-brain"
    }}>{`The sleepy brain`}</h2>
    <p>{`But even programmers should be sleeping at night. We are not some race of super humans. Even programmers feel more alert during the day.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/ballmer_peak1.png",
        "alt": "Ballmer's peak",
        "title": "Ballmer's peak"
      }}></img></p>
    <p>{`Why then do we perform our most mentally complex work work when the brain wants to sleep and we do simpler tasks when our brain is at its sharpest and brightest?`}</p>
    <p>{`Because being tired makes us better coders.`}</p>
    <p>{`Similar to the ballmer peak, being tired can make us focus better simply because when your brain is tired it `}<em parentName="p">{`has`}</em>{` to focus! There isn't enough left-over brainpower to afford losing concentration.`}</p>
    <p>{`I seem to get the least work done right after drinking too much tea or having a poorly timed `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Energy_drink",
        "title": "Energy drink"
      }}>{`energy drink`}</a>{`. Makes me hyperactive and one second I'm checking twitter, the next I'm looking at hacker news and I just seem to be buzzing all over the place..`}</p>
    <p>{`You'd think I'd work better - so much energy, so much infinite overclocked brainpower. But instead I keep tripping over myself because I can't focus for more than two seconds at a time.`}</p>
    <p>{`Conversely, when I'm slightly tired, I just plomp my arse down and `}<em parentName="p">{`code`}</em>{`. With a slightly tired brain I can code for hours and hours without even thinking about checking twitter or facebook. It's like the internet stops existing.`}</p>
    <p>{`I feel like this holds true for most programmers out there. We have too much brainpower for ~80% of the tasks we work on - face it, writing that one juicy algorithm, requires ten times as much code to produce an environment in which it can run. Even if you're doing the most advanced machine learning (or something) imaginable, a lot of the work is simply cleaning up the data and presenting results in a lovely manner.`}</p>
    <p>{`And when your brain isn't working at full capacity it looks for something to do. Being tired makes you dumb enough that the task at hand is enough.`}</p>
    <h2 {...{
      "id": "bright-computer-screens"
    }}>{`Bright computer screens`}</h2>
    <p>{`This one is pretty simple. Keep staring at a bright source of light in the evening and your `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Circadian_rhythm",
        "title": "Circadian rhythm"
      }}>{`sleep cycle`}</a>{` gets delayed. You forget to be tired until 3am. Then you wake up at 11am and when the evening rolls around you simply aren't tired because hey, you've only been up since 11am!`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/300px-Innsbrucklarge5.jpg",
        "alt": "A city",
        "title": "A city"
      }}></img></p>
    <p>{`Given enough iterations this can essentially drag you into a different timezone. What's more interesting is that it doesn't seem to keep rolling, once you get into that equilibrium of going to bed between 3am and 4am you tend to stay there.`}</p>
    <p>{`Or maybe that's just the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Alarm_clock",
        "title": "Alarm clock"
      }}>{`alarm clocks`}</a>{` doing their thing because society tells us we're dirty dirty slobs if we have breakfast at 2pm.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`To conclude, programmers work at night because it doesn't impose a time limit on when you have to stop working, which gives you a more relaxed approach, your brain doesn't keep looking for distractions and a bright screen keeps you awake.`}</p>
    <p>{`You should sign up by email to receive tips and science about productivity and keeping your sanity as a developer, `}<a parentName="p" {...{
        "href": "http://swiz.ec/nightowls-list"
      }}>{`here`}</a>{`.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://paulgraham.com/head.html"
        }}>{`Holding a Program in One's Head (2007)`}</a>{` (paulgraham.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://cbloomrants.blogspot.com/2011/11/11-22-11-mature-programmer.html"
        }}>{`The Mature Programmer`}</a>{` (cbloomrants.blogspot.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://web.archive.org/web/20120207190436/http://computersight.com/programming/so-you-want-to-be-a-computer-programmer/"
        }}>{`So You Want to be a Computer Programmer`}</a>{` (computersight.com) `}<em parentName="li">{`Web Archive`}</em></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/programmers-are-fucking-lazy/swizec/2648"
        }}>{`Programmers are fucking lazy`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://web.archive.org/web/20120304100937/http://blog-johnfn.herokuapp.com/entry/4"
        }}>{`How to be the 10X programmer`}</a>{` (blog-johnfn.herokuapp.com) `}<em parentName="li">{`Web Archive`}</em></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://web.archive.org/web/20160308062810/http://bob-roberts.net/2011/11/06/whats-in-a-name-programmer-or-developer/"
        }}>{`What's in a name? Programmer or Developer`}</a>{` (bob-roberts.net) `}<em parentName="li">{`Web Archive`}</em></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.jpl-consulting.com/2011/12/why-i-will-never-feel-threatened-by-programmers-in-india/"
        }}>{`Why I Will Never Feel Threatened by Programmers in India`}</a>{` (jpl-consulting.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=09e13045-a79a-44fa-a93e-3fde6634f398",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      